import { PcmResampler } from './pcm';
import type { ActionEvent, ClientDiagnostics } from './types';

interface ReadingContext { csrfToken: string; sessionId: string; actionEvents: ActionEvent[]; diagnostics: ClientDiagnostics }
export interface ReadingReport { clientPassed: boolean; matchedChars: number; totalChars: number; durationSeconds: number }

/** 自建服务只要求标准音频采集能力，不再依赖浏览器厂商的语音识别服务。 */
export function supportsSpeechRecognition(): boolean {
  return typeof AudioContext !== 'undefined' && typeof AudioWorkletNode !== 'undefined' && typeof WebSocket !== 'undefined';
}

/** 仅通过同源 WSS 发送 PCM 音频，视频 Blob 不进入此类；共享麦克风的 track 不在这里停止。 */
export class SpeechReader {
  private socket: WebSocket | null = null;
  private audio: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private resampler: PcmResampler | null = null;
  private pending: Array<(error: Error) => void> = [];
  private onFinal: (() => void) | null = null;
  private onSaved: ((passed: boolean) => void) | null = null;
  private onFlushed: (() => void) | null = null;
  private audioQueue: number[] = [];
  private stopped = false;
  private failure: ((message: string) => void) | null = null;

  start(stream: MediaStream, context: ReadingContext, onText: (finalText: string, interimText: string) => void, onFailure: (message: string) => void): Promise<void> {
    this.cancel();
    if (!supportsSpeechRecognition()) return Promise.reject(new Error('当前浏览器不支持实时音频采集，请使用较新的浏览器并通过安全网址访问'));
    this.failure = onFailure;
    this.stopped = false;
    this.audioQueue = [];
    const audio = new AudioContext();
    this.audio = audio;
    // 在点击事件中申请音频运行资格，不能等网络握手后才 resume（移动端会丢失用户手势）。
    const resumed = audio.resume();
    void resumed.catch(() => undefined);
    const url = new URL('/api/verification/commitment/read', location.href);
    url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(url);
    this.socket = socket;
    const ready = this.wait<void>((resolve, reject) => {
      let started = false;
      let saved = false;
      const fail = (message: string) => {
        if (this.socket !== socket) return;
        onFailure(message);
        reject(new Error(message));
        this.cancel(new Error(message));
      };
      socket.onopen = () => socket.send(JSON.stringify({ type: 'start', ...context }));
      socket.onerror = () => fail('语音连接失败，请检查网络或联系管理员检查语音服务');
      socket.onclose = () => {
        if (this.socket === socket && !saved) fail('语音连接已中断，本次不能通过，请重新核验');
      };
      socket.onmessage = (event) => {
        if (this.socket !== socket) return;
        let response: { type: string; text?: string; message?: string; passed?: boolean };
        try { response = JSON.parse(String(event.data)); } catch { fail('语音服务返回异常，请重新核验'); return; }
        if (response.type === 'error') { fail(response.message || '语音服务暂不可用，请稍后重试'); return; }
        if (response.type === 'ready' && !started) {
          started = true;
          void resumed.then(() => audio.audioWorklet.addModule('/pcm-worklet.js')).then(() => {
            if (this.socket !== socket) return;
            this.resampler = new PcmResampler(audio.sampleRate);
            this.source = audio.createMediaStreamSource(stream);
            this.worklet = new AudioWorkletNode(audio, 'commitment-pcm');
            this.worklet.port.onmessage = ({ data }: MessageEvent<Float32Array | 'flushed'>) => {
              if (data === 'flushed') { this.onFlushed?.(); this.onFlushed = null; return; }
              if (this.socket !== socket || this.stopped) return;
              const pcm = this.resampler!.process(data);
              this.audioQueue.push(...pcm);
              this.sendAudio(false);
            };
            this.source.connect(this.worklet);
            // Worklet 输出全零，只为持续驱动采样，不把自己的声音从扬声器播放。
            this.worklet.connect(audio.destination);
            resolve();
          }).catch(() => fail('麦克风音频采集启动失败，请检查浏览器权限后重新核验'));
        } else if (response.type === 'partial') onText('', response.text ?? '');
        else if (response.type === 'final') { onText(response.text ?? '', ''); this.onFinal?.(); this.onFinal = null; }
        else if (response.type === 'saved') { saved = true; this.onSaved?.(response.passed === true); this.onSaved = null; }
      };
    }, 18000, '语音服务启动超时，请检查服务状态或稍后重试');
    return ready.catch(error => { if (this.socket === socket) this.cancel(error); throw error; });
  }

  private sendAudio(flush: boolean): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    // 网络跟不上时中断，不无限积压语音，也不把丢帧后的文本判为通过。
    if (socket.bufferedAmount > 128000) {
      const message = '网络发送过慢，语音数据积压，请重新核验';
      this.failure?.(message);
      this.cancel(new Error(message));
      return;
    }
    while (this.audioQueue.length >= 3200 || (flush && this.audioQueue.length)) {
      const chunk = this.audioQueue.splice(0, 3200);
      const bytes = new ArrayBuffer(chunk.length * 2);
      const view = new DataView(bytes);
      chunk.forEach((sample, index) => view.setInt16(index * 2, sample, true));
      socket.send(bytes);
    }
  }

  async finish(): Promise<void> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) throw new Error('语音连接已断开，请重新核验');
    if (this.worklet) await this.wait<void>(resolve => {
      this.onFlushed = resolve;
      this.worklet!.port.postMessage('flush');
    }, 1500, '麦克风收尾超时，请重新核验');
    this.stopped = true;
    this.detachAudio();
    this.sendAudio(true);
    return this.wait<void>((resolve) => {
      this.onFinal = resolve;
      this.socket?.send(JSON.stringify({ type: 'stop' }));
    }, 10000, '语音识别未返回最终结果，请重新核验');
  }

  report(report: ReadingReport): Promise<boolean> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return Promise.reject(new Error('核验记录未能保存，请重新核验'));
    return this.wait<boolean>((resolve) => {
      this.onSaved = resolve;
      this.socket!.send(JSON.stringify({ type: 'report', report }));
    }, 6000, '核验记录保存超时，请在管理后台核对后重新核验');
  }

  private wait<T>(setup: (resolve: (value: T | PromiseLike<T>) => void, reject: (error: Error) => void) => void, timeout: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const done = () => { window.clearTimeout(timer); this.pending = this.pending.filter(item => item !== failure); };
      const failure = (error: Error) => { done(); reject(error); };
      const timer = window.setTimeout(() => failure(new Error(message)), timeout);
      this.pending.push(failure);
      setup(value => { done(); resolve(value); }, failure);
    });
  }

  private detachAudio(): void {
    if (this.worklet) { this.worklet.port.onmessage = null; this.worklet.disconnect(); }
    this.source?.disconnect();
    void this.audio?.close().catch(() => undefined);
    this.audio = null;
    this.source = null;
    this.worklet = null;
  }

  cancel(error = new Error('语音核验已取消')): void {
    const socket = this.socket;
    this.socket = null;
    this.detachAudio();
    this.stopped = true;
    this.audioQueue = [];
    this.pending.slice().forEach(reject => reject(error));
    this.onFinal = this.onSaved = null;
    this.onFlushed = null;
    this.failure = null;
    if (socket) { socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null; socket.close(); }
  }
}
